import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV } from "../content";
import { scrollToId } from "./SmoothScroll";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open ? "bg-[#0a0c0e]/80 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <button onClick={() => go("top")} data-testid="nav-logo" aria-label="PLUK HR home">
          <Logo />
        </button>
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-link-${n.id}`}
              className="group relative text-sm text-zinc-300 hover:text-white transition-colors"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-emerald-400 transition-transform duration-500 group-hover:scale-x-100" />
            </button>
          ))}
          <Link to="/careers" className="group relative text-sm text-emerald-300 hover:text-emerald-200 transition-colors" data-testid="nav-link-careers">
            Careers
            <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-emerald-400 transition-transform duration-500 group-hover:scale-x-100" />
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={() => go("mandate")}
            data-testid="nav-submit-mandate-btn"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-medium text-[#0a0c0e] transition-colors hover:bg-emerald-400"
          >
            Share a mandate
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            className="lg:hidden rounded-full border border-white/15 p-2.5"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-6 gap-1">
              {[...NAV, { id: "mandate", label: "Share a mandate" }].map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  data-testid={`nav-mobile-link-${n.id}`}
                  className="font-display text-left text-3xl py-2 text-zinc-100"
                >
                  {n.label}
                </button>
              ))}
              <Link to="/careers" className="font-display text-left text-3xl py-2 text-emerald-300" data-testid="nav-mobile-link-careers">Careers</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
